import * as z from 'zod';

import { addressQuerySchema } from '@/_containers/inputs/address/shared/address-query-schema';
import { contactQueryLinkerSchema } from '@/_pages/contacts/shared';
import { billingTermQueryLinkerSchema } from '@/_pages/settings/billing-terms/shared/billing-term-query-linker-schema';
import { brandQueryLinkerSchema } from '@/_pages/settings/brands/shared/';
import { partnerQueryLinkerSchema } from '@/_pages/settings/partners/shared';
import { tcTemplateQueryLinkerSchema } from '@/_pages/settings/terms-templates/shared/schemas';
import { addendumTableSchema } from '@/_schemas/agreements/addendum-query-schema';
import { agreementQueryLinkerSchema } from '@/_schemas/agreements/agreements-query-linker-schema';
import { fileQuerySchema } from '@/_schemas/file';
import { booleanQuerySchema, nullableNumericQuerySchema, paginatedResponseSchema } from '@/_schemas/generic';
import { teamMemberQueryLinkerWithContactInfoSchema } from '@/_schemas/team-members/team-members-query-linker-with-contact-info-schema';

import { commissionsQuerySchema } from './commissions-schemas';
import { clientQuerySchemaAgreementsLinker } from './linked-query-schemas';
import { subsidyQuerySchema } from './subsidy-schemas';

import { contractingPartyQuerySchema } from '../components/contracting-party/schema';
import { deliverablesQuerySchema } from '../deliverables';
import { agreementInstallmentsQuerySchema } from '../installments/schemas/agreement-installments-query-schema';

export const agreementQuerySchema = z.object({
  ...agreementQueryLinkerSchema.shape,

  purchase_order: z.string(),
  opt_in: z.number(),
  one_time_total: z.number(),
  billing_term_id: z.number().nullable(),
  brand_id: z.number().nullable(),
  partner_id: z.number().nullable(),

  account_manager: z.array(teamMemberQueryLinkerWithContactInfoSchema),

  client_id: z.number(),
  client: clientQuerySchemaAgreementsLinker,
  primary_domains: clientQuerySchemaAgreementsLinker.shape.domains.array().element,

  agreement_contact: clientQuerySchemaAgreementsLinker.shape.contacts.array().element,
  project_contact: clientQuerySchemaAgreementsLinker.shape.contacts.array().element,
  billing_contact: clientQuerySchemaAgreementsLinker.shape.contacts.array().element,

  emergency_contact: clientQuerySchemaAgreementsLinker.shape.contacts,
  billing_email_contacts: clientQuerySchemaAgreementsLinker.shape.contacts,

  billing_term: billingTermQueryLinkerSchema.nullable(),

  brand: brandQueryLinkerSchema.nullable(),

  partner: partnerQueryLinkerSchema.nullable(),

  terms_condition_template_id: z.number().nullable(),
  terms_condition_template: tcTemplateQueryLinkerSchema.nullable(),

  harvest: nullableNumericQuerySchema(),
  agreement_draft: z.array(fileQuerySchema).nullish(),
  fully_executed_agreement: z.array(fileQuerySchema).nullish(),

  implementation_start_date: z.string().nullish(),
  go_live_date: z.string().nullish(),
  monthly_recurring_start_date: z.string().nullish(),
  monthly_recurring_stop_date: z.string().nullish(),
  annual_recurring_start_date: z.string().nullish(),
  annual_recurring_stop_date: z.string().nullish(),

  agreement_signer: contactQueryLinkerSchema.nullable(),
  agreement_signer_id: z.number().nullable(),

  agreement_installments: z.array(agreementInstallmentsQuerySchema).nullish(),

  agreement_deliverables: z.array(deliverablesQuerySchema),
  wholesale_price_enabled: booleanQuerySchema(),

  selectedWholesaleDateRange: z
    .object({
      start_date: z.string().nullable(),
      end_date: z.string().nullable(),
    })
    .nullable(),

  addresses: z.array(addressQuerySchema).nullable(),
  parent: agreementQueryLinkerSchema.nullable(),
  addendums: z.array(addendumTableSchema).nullish(),

  exclude_from_averages: booleanQuerySchema(),

  enable_commission: booleanQuerySchema(),
  agreement_commissions: z.array(commissionsQuerySchema).nullable(),
  // subsidy fields (not used in main agreement schema but kept for reference)
  activate_subsidy_billing: booleanQuerySchema().nullish(),
  subsidies: z.array(subsidyQuerySchema).nullish(),

  delay_allocating_revenue: z.boolean().nullish(),
  hide_taxable_column_from_invoices: z.boolean().nullish(),

  contracting_party: contractingPartyQuerySchema.nullable(),

  orphan_invoicing: booleanQuerySchema(),
});

export type AgreementQuerySchema = z.infer<typeof agreementQuerySchema>;

export const agreementsAllQuerySchema = paginatedResponseSchema(agreementQuerySchema);

export type AgreementsAllQuerySchema = z.infer<typeof agreementsAllQuerySchema>;

export const ONE_TIME = 'One-Time';
export const MONTHLY = 'Monthly';
export const ANNUAL = 'Annual';
